import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/AllCustomers.css';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { toast } from 'react-toastify';
import {
  FaCheckCircle,
  FaEnvelope,
  FaSearch,
  FaTimes,
  FaUserFriends,
  FaUserLock,
} from 'react-icons/fa';
import { FiExternalLink } from 'react-icons/fi';
import PromotionalEmailModal from './PromotionalEmailModal';
import CustomerProfile from './CustomerProfile';
import { useAdminListOverlay } from '../../../utils/useAdminPageSearch';
import { buildAdminPageSearch } from '../../../utils/adminDashboardNav';
import {
  customerInitials,
  formatDate,
  isPendingLead,
} from './customerUtils';

const GET_CUSTOMERS = `query GetCustomer { getCustomer { id fullName email phone address postcode status createdAt portalCredentialsSentAt linkedBookingIds approvedLeadCount } }`;
const SEND_CREDENTIALS = `mutation ClientRegister($email: String!) { clientRegister(createClientLoginInput: { email: $email }) }`;
const UPDATE_CUSTOMER = `mutation UpdateCustomerProfile($input: UpdateCustomerProfileInput!) { updateCustomerProfile(input: $input) { id fullName email phone address postcode } }`;
const FIND_PENDING_BOOKINGS = `
  query FindCustomerListBookings($page: Float!, $limit: Float!) {
    findAllBooking(page: $page, limit: $limit, includeSignUrls: false) {
      data {
        id
        fullName
        email
        status
        createdAt
        preferredDate
        message
        bookingFrequency
        recordType
      }
    }
  }
`;

function CustomerEditModal({ customer, onClose, onSaved }) {
  const [form, setForm] = useState({
    fullName: customer.fullName || '',
    email: customer.email || '',
    phone: customer.phone || '',
    address: customer.address || '',
    postcode: customer.postcode || '',
  });
  const [saving, setSaving] = useState(false);
  const field = (name) => (e) => setForm((prev) => ({ ...prev, [name]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.fullName.trim() || !form.email.trim())
      return toast.error('Customer name and email are required.');
    try {
      setSaving(true);
      await graphqlRequest(UPDATE_CUSTOMER, {
        input: { customerId: customer.id, ...form },
      });
      toast.success('Customer profile updated across linked records.');
      await onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Failed to update customer profile.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="customer-modal-overlay" onClick={onClose} role="presentation">
      <form
        className="customer-edit-modal"
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
      >
        <header>
          <div>
            <h2>Edit customer</h2>
            <p>Updates every linked approved customer record.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </header>
        <div className="customer-edit-grid">
          <label>
            <span>Full name *</span>
            <input value={form.fullName} onChange={field('fullName')} />
          </label>
          <label>
            <span>Email *</span>
            <input type="email" value={form.email} onChange={field('email')} />
          </label>
          <label>
            <span>Phone</span>
            <input value={form.phone} onChange={field('phone')} />
          </label>
          <label>
            <span>Postcode</span>
            <input value={form.postcode} onChange={field('postcode')} />
          </label>
          <label className="wide">
            <span>Address</span>
            <input value={form.address} onChange={field('address')} />
          </label>
        </div>
        <footer>
          <button type="button" className="customer-cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="customer-save-btn" disabled={saving}>
            {saving ? 'Saving…' : 'Save customer'}
          </button>
        </footer>
      </form>
    </div>
  );
}

export default function AllCustomers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [pendingLeads, setPendingLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sendingFor, setSendingFor] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [search, setSearch] = useState('');
  const [portalFilter, setPortalFilter] = useState('all');
  const [showAllNotices, setShowAllNotices] = useState(false);
  const overlay = useAdminListOverlay(customers);

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [res, leadsRes] = await Promise.all([
        graphqlRequest(GET_CUSTOMERS),
        graphqlRequest(FIND_PENDING_BOOKINGS, { page: 1, limit: 100 }).catch(
          () => null,
        ),
      ]);
      setCustomers(res.getCustomer || []);
      setPendingLeads(
        (leadsRes?.findAllBooking?.data || []).filter(isPendingLead),
      );
    } catch (err) {
      console.error(err);
      setError('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return customers.filter((c) => {
      const portal =
        portalFilter === 'all' ||
        (portalFilter === 'active'
          ? c.portalCredentialsSentAt
          : !c.portalCredentialsSentAt);
      return (
        portal &&
        (!term ||
          [c.fullName, c.email, c.phone, c.address]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(term)))
      );
    });
  }, [customers, portalFilter, search]);

  const stats = useMemo(
    () => ({
      total: customers.length,
      portal: customers.filter((c) => c.portalCredentialsSentAt).length,
      repeat: customers.filter((c) => Number(c.approvedLeadCount) > 1).length,
      incomplete: customers.filter((c) => !c.email || !c.phone || !c.address)
        .length,
    }),
    [customers],
  );

  /** Email is the customer's unique key, so one lead maps to one customer. */
  const matchCustomer = (rows, lead) => {
    const email = String(lead?.email || '').trim().toLowerCase();
    if (!email) return undefined;
    return rows.find(
      (c) => String(c.email || '').trim().toLowerCase() === email,
    );
  };

  const notices = useMemo(() => {
    const leadCards = pendingLeads.slice(0, 8).map((lead) => ({
      id: `lead-${lead.id}`,
      kind: 'lead',
      title: 'Booking Request',
      name: lead.fullName || 'Unknown',
      email: lead.email,
      detail: lead.bookingFrequency || lead.message || 'New enquiry',
      leadId: lead.id,
      customer: matchCustomer(customers, lead),
    }));
    const incompleteCards = customers
      .filter((c) => !c.email || !c.phone || !c.address)
      .slice(0, 4)
      .map((c) => ({
        id: `inc-${c.id}`,
        kind: 'profile',
        title: 'Profile to complete',
        name: c.fullName || 'Unnamed',
        email: c.email,
        detail: 'Missing contact or address details',
        customer: c,
      }));
    const merged = [...leadCards];
    incompleteCards.forEach((card) => {
      if (!merged.some((row) => row.email && row.email === card.email)) {
        merged.push(card);
      }
    });
    return merged;
  }, [customers, pendingLeads]);

  const visibleNotices = showAllNotices ? notices : notices.slice(0, 2);

  const allSelected =
    filtered.length > 0 && filtered.every((c) => selectedIds.has(c.id));
  const toggleAll = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      filtered.forEach((c) =>
        allSelected ? next.delete(c.id) : next.add(c.id),
      );
      return next;
    });
  const toggleOne = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const sendInvite = async (c) => {
    if (!c.email) return toast.error('Customer email not found.');
    setSendingFor(c.id);
    try {
      await graphqlRequest(SEND_CREDENTIALS, {
        email: c.email.trim().toLowerCase(),
      });
      toast.success(`Credentials sent to ${c.email}`);
      await fetchCustomers();
    } catch (err) {
      toast.error(err?.message || 'Failed to send credentials.');
    } finally {
      setSendingFor(null);
    }
  };

  const openNotice = (notice) => {
    if (notice.customer) {
      overlay.openView(notice.customer);
      return;
    }
    if (notice.leadId) {
      navigate({
        pathname: '/',
        search: buildAdminPageSearch('lead', {
          search: notice.email || notice.name,
        }),
      });
    }
  };

  if (overlay.viewing) {
    return (
      <>
        <CustomerProfile
          customer={overlay.viewing}
          onBack={overlay.closeOverlay}
          onEdit={() =>
            overlay.patch(
              { view: overlay.viewing.id, edit: overlay.viewing.id },
              ['action', 'history'],
            )
          }
        />
        {overlay.editing ? (
          <CustomerEditModal
            customer={overlay.editing}
            onClose={() => overlay.patch({}, ['edit'])}
            onSaved={fetchCustomers}
          />
        ) : null}
      </>
    );
  }

  if (loading && !customers.length)
    return <div className="loader">Loading customers...</div>;

  return (
    <div className="customers-container">
      <p className="cm-crumb">
        SAS Admin <span>/</span> Customer Management
      </p>
      <div className="customers-top-bar">
        <div>
          <h1 className="customers-title">Customer Management</h1>
          <p className="customers-subtitle">
            Manage customer profiles, portal access and communication.
          </p>
        </div>
        <div className="customers-top-actions">
          {selectedIds.size ? (
            <span className="customers-selected-count">
              {selectedIds.size} selected
            </span>
          ) : null}
          <button
            type="button"
            className="promo-email-btn"
            onClick={() => setShowPromoModal(true)}
            disabled={!customers.length}
          >
            <FaEnvelope /> Promotional Email
          </button>
        </div>
      </div>

      {notices.length ? (
        <section className="cm-notice">
          <div className="cm-notice-head">
            <h2>Quick Notification</h2>
            {notices.length > 2 ? (
              <button
                type="button"
                className="cm-notice-all"
                onClick={() => setShowAllNotices((open) => !open)}
              >
                {showAllNotices ? 'Show less' : 'View all'}
              </button>
            ) : null}
          </div>
          <div className="cm-notice-list">
            {visibleNotices.map((notice) => (
              <article className="cm-notice-card" key={notice.id}>
                <span className="cm-notice-avatar">
                  {customerInitials(notice.name)}
                </span>
                <div className="cm-notice-copy">
                  <strong>
                    {notice.name}
                    {notice.email ? <small>{notice.email}</small> : null}
                  </strong>
                  <p>
                    <b>{notice.title}</b>
                    {notice.detail}
                  </p>
                  <button
                    type="button"
                    className="cm-notice-view"
                    onClick={() => openNotice(notice)}
                  >
                    <FiExternalLink /> View Now
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      <div className="customer-kpi-grid">
        <article>
          <span className="customer-kpi-icon purple">
            <FaUserFriends />
          </span>
          <div>
            <strong>{stats.total}</strong>
            <p>Total Customers</p>
          </div>
        </article>
        <article>
          <span className="customer-kpi-icon blue">
            <FaUserLock />
          </span>
          <div>
            <strong>{stats.portal}</strong>
            <p>Portal Enabled</p>
          </div>
        </article>
        <article>
          <span className="customer-kpi-icon green">
            <FaEnvelope />
          </span>
          <div>
            <strong>{stats.repeat}</strong>
            <p>Repeat Customers</p>
          </div>
        </article>
        <article>
          <span className="customer-kpi-icon amber">
            <FaCheckCircle />
          </span>
          <div>
            <strong>{stats.incomplete}</strong>
            <p>Profiles to Complete</p>
          </div>
        </article>
      </div>

      <div className="customer-toolbar">
        <label className="customer-search">
          <FaSearch />
          <input
            type="search"
            placeholder="Search name, email, phone or address…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <select
          value={portalFilter}
          onChange={(e) => setPortalFilter(e.target.value)}
        >
          <option value="all">All portal statuses</option>
          <option value="active">Portal enabled</option>
          <option value="not-invited">Not invited</option>
        </select>
      </div>

      {error ? (
        <div className="customer-error">
          <span>{error}</span>
          <button type="button" onClick={fetchCustomers}>
            Try again
          </button>
        </div>
      ) : null}
      {!error && !filtered.length ? (
        <div className="customer-empty">
          <FaUserFriends />
          <h3>No customers found</h3>
          <p>Try changing your search or portal filter.</p>
        </div>
      ) : null}
      {!error && filtered.length ? (
        <div className="table-wrapper">
          <table className="customers-table">
            <thead>
              <tr>
                <th className="checkbox-col">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    aria-label="Select all customers"
                  />
                </th>
                <th>Customer</th>
                <th>Added date</th>
                <th>Contact</th>
                <th>Address</th>
                <th>Projects</th>
                <th>Portal</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr
                  key={c.id}
                  className={selectedIds.has(c.id) ? 'row-selected' : ''}
                >
                  <td className="checkbox-col">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(c.id)}
                      onChange={() => toggleOne(c.id)}
                      aria-label={`Select ${c.fullName || 'customer'}`}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="customer-name-cell customer-name-btn"
                      onClick={() => overlay.openView(c)}
                    >
                      <span className="customer-table-avatar">
                        {customerInitials(c.fullName)}
                      </span>
                      <div>
                        <strong>{c.fullName || 'Unnamed'}</strong>
                        <small>{c.email || 'No email'}</small>
                      </div>
                    </button>
                  </td>
                  <td>{formatDate(c.createdAt)}</td>
                  <td>
                    {c.phone ? (
                      <a href={`tel:${c.phone}`}>{c.phone}</a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    {c.address || '—'}
                    {c.postcode ? <small>{c.postcode}</small> : null}
                  </td>
                  <td>
                    <span className="cm-project-badge">
                      {c.approvedLeadCount || 1}
                    </span>
                  </td>
                  <td>
                    {c.portalCredentialsSentAt ? (
                      <span className="credentials-sent-badge">Enabled</span>
                    ) : (
                      <button
                        type="button"
                        className="send-credentials-btn"
                        disabled={sendingFor === c.id || !c.email}
                        onClick={() => sendInvite(c)}
                      >
                        {sendingFor === c.id ? 'Sending…' : 'Send invite'}
                      </button>
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="cm-view-btn"
                      onClick={() => overlay.openView(c)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {showPromoModal ? (
        <PromotionalEmailModal
          customers={customers}
          selectedIds={selectedIds}
          onClose={() => setShowPromoModal(false)}
          onSent={(r) =>
            toast.success(
              r?.message || `Queued ${r?.queued || 0} promotional email(s).`,
            )
          }
        />
      ) : null}
      {overlay.editing ? (
        <CustomerEditModal
          customer={overlay.editing}
          onClose={overlay.closeOverlay}
          onSaved={fetchCustomers}
        />
      ) : null}
    </div>
  );
}
